import {
  createFileRoute,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import { LoadingComponent } from '@/components/shared-ui/loading-component';
import { useKeepAuthSessionSubscribed } from '@/services/auth-service';
import { DashboardSkeleton } from '@/components/web-fe/pages/dashboard/dashboard-skeleton';

export const Route = createFileRoute('/app')({
  component: Page,
  ssr: false,
  // `/app` is the single `ssr: false` boundary for the whole app subtree, so
  // its `pendingComponent` is what TanStack Start server-renders as the SSR
  // fallback for EVERY `/app/*` hard load (selective-SSR "Fallback Rendering"
  // docs). It can't be route-specific at SSR time because the server doesn't run
  // the children's `beforeLoad`/`loader` under `ssr: false` — it only knows this
  // boundary route. So we branch on the requested path: auth screens keep the
  // neutral spinner, and everything else (the dashboard) first-paints the
  // dashboard skeleton, matching the `_authenticated` pending state that follows
  // on the client.
  //
  // TODO: The clean fix is to move the `ssr: false` boundary DOWN — put it on
  // `_authenticated` (with `pendingComponent: DashboardSkeleton`) and on the
  // `(auth)` routes (with a login skeleton) — so each section declares its own
  // SSR fallback natively and this path-branching goes away. Deferred for now;
  // verify why `/app` is `ssr: false` before moving it.
  pendingComponent: AppRoutePending,
  pendingMs: 0,
});

// Auth screens under `/app` (login/continue/register). Substring match so it
// also covers locale-prefixed paths (e.g. `/de/app/login`) if delocalization
// hasn't been applied to the router location yet.
const AUTH_SCREEN_PATTERN = /\/app\/(login|continue|register|connect)\b/;

function AppRoutePending() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return AUTH_SCREEN_PATTERN.test(pathname) ? (
    <LoadingComponent />
  ) : (
    <DashboardSkeleton />
  );
}

function Page() {
  // Declared on the `/app` layout, the one component mounted across the whole
  // subtree — including the `_authenticated` -> `(auth)` transition a logout
  // performs, which is where the store used to go dormant. See the hook.
  useKeepAuthSessionSubscribed();

  return (
    <div
      id='app'
      className='relative h-dvh w-full overflow-hidden bg-violet-500 dark:bg-navy-760'
    >
      <Outlet />
    </div>
  );
}
